import { useLocation, useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button, LoadingIndicator } from '@cfa/react-core';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { invalidateAllPlanQueries } from '@/utils/queryInvalidation';

import GenericError from '@/components/Error/GenericError';

import Constants, { routes } from 'constants/index';
import { isApiError } from 'utils/request';

import {
  trainTogetherUpdateStepStatus,
  updateStepStatus,
} from '@/_generated/api-clients/xp';

export interface LocationProps {
  state: {
    planId: string;
    step: {
      id: string;
    };
    userIds?: string[];
    stepStatus?: string;
  };
}

const DocumentCompleteButton = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    mutateAsync: updateTrainTogetherStepStatusMutation,
    isPending: updateTrainTogetherStepStatusLoading,
  } = useMutation({
    mutationFn: async (variables: {
      planId: string;
      stepId: string;
      body: { status: 'COMPLETE'; userIds: string[] };
    }) => {
      const { data } = await trainTogetherUpdateStepStatus({
        path: { planId: variables.planId, stepId: variables.stepId },
        body: variables.body,
      });
      return data;
    },
  });

  const {
    mutateAsync: updatePlanStepStatusMutation,
    isPending: updatePlanStepStatusLoading,
  } = useMutation({
    mutationFn: async (variables: {
      planId: string;
      stepId: string;
      body: { status: 'COMPLETE' };
    }) => {
      const { data } = await updateStepStatus({
        path: { planId: variables.planId, stepId: variables.stepId },
        body: variables.body,
      });
      return data;
    },
  });

  const isLoading =
    updateTrainTogetherStepStatusLoading || updatePlanStepStatusLoading;

  const { planId, step, userIds } = (location as LocationProps).state;

  const onCompleteButtonClick = () => {
    const handleResponse = (promise: Promise<unknown>) => {
      promise
        .then(async () => {
          if (!isValidPlanId(planId)) {
            throw new Error('Invalid planId');
          }
          await invalidateAllPlanQueries(planId);
          if (!userIds) {
            navigate({
              pathname: routes.plans.plan({ planId }),
            });
          } else {
            navigate(-1);
          }
        })
        .catch((error: unknown) => {
          if (isApiError(error)) {
            datadogRum.addError(error);
            return <GenericError />;
          }
          datadogRum.addError(error);
        });
    };

    if (userIds) {
      handleResponse(
        updateTrainTogetherStepStatusMutation({
          planId,
          stepId: step.id,
          body: {
            status: 'COMPLETE',
            userIds,
          },
        }),
      );
    } else {
      handleResponse(
        updatePlanStepStatusMutation({
          planId,
          stepId: step.id,
          body: {
            status: 'COMPLETE',
          },
        }),
      );
    }
  };

  return (location as LocationProps).state.stepStatus !==
    Constants.TRAINING_PLANS.COMPLETE ? (
    <Button
      isDisabled={isLoading}
      color="secondary"
      onPressEnd={onCompleteButtonClick}
      data-testid="CompleteDocumentButton"
    >
      {isLoading && <LoadingIndicator size="sm" />}
      {t(isLoading ? 'Generic.loading' : 'Button.markCompleted')}
    </Button>
  ) : (
    <Button
      color="primary"
      isDisabled
      variant="text"
      data-testid="CompletedDocumentButton"
    >
      {t('Button.completed')}
    </Button>
  );
};

export default DocumentCompleteButton;
